import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Github, Pencil, ShieldCheck, UserCheck, UserPlus, UserX, Users } from 'lucide-react';
import { usersAPI } from '../services/api';
import { Drawer, Modal, Popconfirm, Table, toast, Form, Field, useForm } from '../../../ui';
import { PageHeader, StatCard, FilterBar, LoadingState, ErrorState } from '../components';
import EmptyState from '../../../components/site/EmptyState';
import { USER_ID } from '../../../configs/configs';
import { formatDate, formatDateTime } from '../../../utils/format';
import { getErrorMessage } from '../../../utils/apiError';
import { getInitials, phoneDigits } from '../utils/helpers';

const ROLES = ['User', 'Admin'];
const STATUSES = ['Active', 'Inactive', 'Suspended'];
const STATUS_TONE = { active: 'success', inactive: undefined, suspended: 'danger' };
const GITHUB_USERNAME = /^[a-zA-Z0-9](?:[a-zA-Z0-9]|-(?=[a-zA-Z0-9])){0,38}$/;

const readCurrentUserId = () => {
  try {
    return localStorage.getItem(USER_ID) || '';
  } catch {
    return '';
  }
};

// The API sends DateTime.MinValue for "never".
const hasDate = value => Boolean(value) && !String(value).startsWith('0001-01-01');

const normalize = (value, allowed) => allowed.find(a => a.toLowerCase() === String(value || '').toLowerCase()) || value || '';

const phoneRule = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    const digits = phoneDigits(value).length;
    return digits >= 10 && digits <= 15
      ? Promise.resolve()
      : Promise.reject(new Error('Enter a 10-digit mobile number (or 10 to 15 digits with country code)'));
  },
};

const githubRule = {
  pattern: GITHUB_USERNAME,
  message: 'Letters, numbers and single hyphens only (no @ or spaces)',
};

const RoleBadge = ({ role }) => {
  const value = normalize(role, ROLES) || 'User';
  return (
    <span className="pk-badge" data-tone={value === 'Admin' ? 'info' : undefined}>
      {value === 'Admin' && <ShieldCheck aria-hidden="true" />}
      {value}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const value = normalize(status, STATUSES) || 'Active';
  return (
    <span className="pk-badge" data-tone={STATUS_TONE[value.toLowerCase()]}>
      {value}
    </span>
  );
};

const GithubLink = ({ username }) =>
  username ? (
    <a
      href={`https://github.com/${encodeURIComponent(username)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="pk-link"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <Github aria-hidden="true" width={14} height={14} />
      {username}
    </a>
  ) : (
    <span className="pk-muted">Not set</span>
  );

/** New account: POST /Users with the same rules as sign-up. */
const AddUserForm = ({ onCancel, onSaved }) => {
  const form = useForm();
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleFinish = async values => {
    setSaving(true);
    try {
      await usersAPI.create({
        fullName: values.fullName.trim(),
        email: (values.email || '').trim() || null,
        phoneNumber: values.phoneNumber.trim(),
        password: values.password,
        gitHubUsername: values.gitHubUsername.trim(),
      });
      toast.success('User created');
      onSaved();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't create the user."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} onFinish={handleFinish} autoComplete="off">
      <Field
        name="fullName"
        label="Full name"
        rules={[
          { required: true, whitespace: true, message: 'Enter a name' },
          { min: 2, max: 50, message: 'Use 2 to 50 characters' },
        ]}
      >
        <input className="pk-input" autoComplete="off" />
      </Field>
      <div className="pk-form__row">
        <Field name="phoneNumber" label="Mobile number" rules={[{ required: true, message: 'Enter a mobile number' }, phoneRule]} help="They sign in with this number.">
          <input className="pk-input" type="tel" inputMode="tel" autoComplete="off" />
        </Field>
        <Field
          name="email"
          label={
            <>
              Email <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[{ type: 'email', message: 'Enter a valid email address' }]}
        >
          <input className="pk-input" type="email" autoComplete="off" />
        </Field>
      </div>
      <Field
        name="gitHubUsername"
        label="GitHub username"
        rules={[{ required: true, whitespace: true, message: 'Enter their GitHub username' }, githubRule]}
        help="Repository access for their purchases goes to this account. It must exist on GitHub."
      >
        <input className="pk-input" autoComplete="off" spellCheck={false} />
      </Field>
      <Field
        name="password"
        label="Password"
        rules={[
          { required: true, message: 'Enter a password' },
          { min: 8, max: 128, message: 'Use 8 to 128 characters' },
          {
            validator: (_, value) =>
              !value || (/[a-zA-Z]/.test(value) && /\d/.test(value))
                ? Promise.resolve()
                : Promise.reject(new Error('Include at least one letter and one number')),
          },
        ]}
      >
        {field => (
          <div style={{ position: 'relative' }}>
            <input
              {...field}
              className="pk-input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              style={{ paddingRight: 48 }}
            />
            <button
              type="button"
              className="adm-icon-btn"
              style={{ position: 'absolute', right: 5, top: 5, border: 0 }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(v => !v)}
            >
              {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
        )}
      </Field>
      <div className="adm-form-foot">
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" disabled={saving} aria-busy={saving || undefined}>
          Create user
        </button>
      </div>
    </Form>
  );
};

/** Edit: PUT /Users/{id} with only the fields that changed. */
const EditUserForm = ({ user, isSelf, onCancel, onSaved }) => {
  const form = useForm();
  const [saving, setSaving] = useState(false);
  const initialValues = useMemo(
    () => ({
      fullName: user.fullName || '',
      phoneNumber: user.phone || '',
      gitHubUsername: user.gitHubUsername || '',
      role: normalize(user.role, ROLES) || 'User',
      status: normalize(user.status, STATUSES) || 'Active',
    }),
    [user]
  );

  const handleFinish = async values => {
    const changes = {};
    const name = values.fullName.trim();
    const phone = values.phoneNumber.trim();
    const github = (values.gitHubUsername || '').trim();
    if (name !== initialValues.fullName) changes.fullName = name;
    if (phone !== initialValues.phoneNumber) changes.phoneNumber = phone;
    if (github && github.toLowerCase() !== initialValues.gitHubUsername.toLowerCase()) changes.gitHubUsername = github;
    if (!isSelf && values.role !== initialValues.role) changes.role = values.role;
    if (!isSelf && values.status !== initialValues.status) changes.status = values.status;

    if (Object.keys(changes).length === 0) {
      toast.info('Nothing changed');
      onCancel();
      return;
    }

    setSaving(true);
    try {
      const response = await usersAPI.update(user.id, changes);
      toast.success(response.data?.message || 'User updated', 6);
      onSaved(response.data?.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't update the user."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish}>
      <Field
        name="fullName"
        label="Full name"
        rules={[
          { required: true, whitespace: true, message: 'Enter a name' },
          { min: 2, max: 50, message: 'Use 2 to 50 characters' },
        ]}
      >
        <input className="pk-input" />
      </Field>
      <div className="pk-form__row">
        <Field
          name="phoneNumber"
          label="Mobile number"
          rules={[{ required: true, message: 'Enter a mobile number' }, phoneRule]}
          help="This is also their sign-in ID."
        >
          <input className="pk-input" type="tel" inputMode="tel" />
        </Field>
        <Field
          name="gitHubUsername"
          label="GitHub username"
          rules={[githubRule]}
          help="Changing it moves repository access for their paid orders to the new account."
        >
          <input className="pk-input" spellCheck={false} />
        </Field>
      </div>
      <div className="pk-form__row">
        <Field name="role" label="Role" help={isSelf ? "You can't change your own role." : undefined}>
          <select className="pk-select" disabled={isSelf}>
            {ROLES.map(role => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </Field>
        <Field
          name="status"
          label="Status"
          help={isSelf ? "You can't deactivate yourself." : 'Only active users can sign in.'}
        >
          <select className="pk-select" disabled={isSelf}>
            {STATUSES.map(status => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="adm-form-foot">
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" disabled={saving} aria-busy={saving || undefined}>
          Save changes
        </button>
      </div>
    </Form>
  );
};

const UserManagement = () => {
  const currentUserId = useMemo(readCurrentUserId, []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersAPI.getAll();
      const list = response.data?.data;
      setUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't load users."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setBusy = (id, busy) =>
    setBusyIds(prev => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const replaceUser = updated => {
    if (!updated?.id) return;
    setUsers(list => list.map(u => (u.id === updated.id ? { ...u, ...updated } : u)));
    setViewing(v => (v?.id === updated.id ? { ...v, ...updated } : v));
  };

  const handleDeactivate = async user => {
    setBusy(user.id, true);
    try {
      const response = await usersAPI.deactivate(user.id);
      replaceUser(response.data?.data || { ...user, status: 'Inactive' });
      toast.success(`${user.fullName || 'User'} can no longer sign in. Their orders are kept.`, 5);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't deactivate the user."));
    } finally {
      setBusy(user.id, false);
    }
  };

  const handleReactivate = async user => {
    setBusy(user.id, true);
    try {
      const response = await usersAPI.update(user.id, { status: 'Active' });
      replaceUser(response.data?.data || { ...user, status: 'Active' });
      toast.success(`${user.fullName || 'User'} can sign in again`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't reactivate the user."));
    } finally {
      setBusy(user.id, false);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter && normalize(u.role, ROLES) !== roleFilter) return false;
      if (statusFilter && normalize(u.status, STATUSES) !== statusFilter) return false;
      if (!term) return true;
      return [u.fullName, u.email, u.phone, u.gitHubUsername].some(v => String(v || '').toLowerCase().includes(term));
    });
  }, [users, search, roleFilter, statusFilter]);

  const stats = useMemo(
    () => ({
      total: users.length,
      active: users.filter(u => normalize(u.status, STATUSES) === 'Active').length,
      admins: users.filter(u => normalize(u.role, ROLES) === 'Admin').length,
      blocked: users.filter(u => normalize(u.status, STATUSES) !== 'Active').length,
    }),
    [users]
  );

  const actionsFor = record => {
    const isSelf = record.id === currentUserId;
    const isActive = normalize(record.status, STATUSES) === 'Active';
    const busy = busyIds.has(record.id);
    const name = record.fullName || 'user';
    return (
      <div className="adm-actions">
        <button type="button" className="adm-icon-btn" aria-label={`View ${name}`} title="View" onClick={() => setViewing(record)}>
          <Eye aria-hidden="true" />
        </button>
        <button type="button" className="adm-icon-btn" aria-label={`Edit ${name}`} title="Edit" onClick={() => setEditing(record)}>
          <Pencil aria-hidden="true" />
        </button>
        {isActive ? (
          <Popconfirm
            title={`Deactivate ${name}?`}
            description="They won't be able to sign in. Orders, invoices and repository access are kept, and you can reactivate them later."
            okText="Deactivate"
            okDanger
            disabled={isSelf || busy}
            onConfirm={() => handleDeactivate(record)}
          >
            <button
              type="button"
              className="adm-icon-btn"
              data-tone="danger"
              aria-label={isSelf ? "You can't deactivate yourself" : `Deactivate ${name}`}
              title={isSelf ? "You can't deactivate yourself" : 'Deactivate'}
              disabled={isSelf || busy}
            >
              <UserX aria-hidden="true" />
            </button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title={`Reactivate ${name}?`}
            description="They'll be able to sign in again."
            okText="Reactivate"
            disabled={busy}
            onConfirm={() => handleReactivate(record)}
          >
            <button type="button" className="adm-icon-btn" aria-label={`Reactivate ${name}`} title="Reactivate" disabled={busy}>
              <UserCheck aria-hidden="true" />
            </button>
          </Popconfirm>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      sorter: (a, b) => (a.fullName || '').localeCompare(b.fullName || ''),
      render: (_, record) => (
        <div className="adm-cell">
          <span className="adm-avatar" aria-hidden="true">
            {getInitials(record.fullName) || '?'}
          </span>
          <div className="adm-cell__text">
            <div className="adm-cell-title">
              {record.fullName || 'No name'}
              {record.id === currentUserId && <span className="pk-muted"> (you)</span>}
            </div>
            <div className="adm-cell-sub">{record.email || 'No email'}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: phone => <span className="adm-num">{phone || '—'}</span>,
    },
    {
      title: 'GitHub',
      dataIndex: 'gitHubUsername',
      key: 'gitHubUsername',
      width: 170,
      render: username => <GithubLink username={username} />,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: role => <RoleBadge role={role} />,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: status => <StatusBadge status={status} />,
    },
    {
      title: 'Joined',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0),
      render: date => <span className="adm-nowrap">{formatDate(date)}</span>,
    },
    {
      title: 'Last sign-in',
      dataIndex: 'lastLoginDate',
      key: 'lastLoginDate',
      width: 130,
      render: date => <span className="adm-nowrap">{hasDate(date) ? formatDate(date) : 'Never'}</span>,
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, record) => actionsFor(record),
    },
  ];

  const hasFilters = Boolean(search || roleFilter || statusFilter);

  let body;
  if (loading) {
    body = <LoadingState label="Loading users" />;
  } else if (error) {
    body = (
      <div className="adm-panel__state">
        <ErrorState title="Couldn't load users" message={error} onRetry={load} />
      </div>
    );
  } else if (users.length === 0) {
    body = (
      <div className="adm-panel__state">
        <EmptyState icon={<Users />} title="No users yet" text="People who sign up on the site appear here." />
      </div>
    );
  } else {
    body = (
      <>
        <FilterBar
          inline
          searchLabel="Search users"
          searchPlaceholder="Search name, email, mobile or GitHub"
          searchValue={search}
          onSearchChange={setSearch}
          filters={[
            {
              key: 'role',
              component: (
                <select className="pk-select" aria-label="Filter by role" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                  <option value="">All roles</option>
                  {ROLES.map(role => (
                    <option key={role} value={role}>
                      {role === 'User' ? 'Customers' : 'Admins'}
                    </option>
                  ))}
                </select>
              ),
            },
            {
              key: 'status',
              component: (
                <select className="pk-select" aria-label="Filter by status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="">Any status</option>
                  {STATUSES.map(status => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              ),
            },
          ]}
          onClearFilters={
            hasFilters
              ? () => {
                  setSearch('');
                  setRoleFilter('');
                  setStatusFilter('');
                }
              : undefined
          }
          end={<span className="adm-toolbar__count">{filtered.length} shown</span>}
        />
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          scroll={{ x: 1100 }}
          emptyText="No users match these filters."
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
        />
      </>
    );
  }

  const viewingIsSelf = viewing?.id === currentUserId;

  return (
    <div>
      <PageHeader
        title="Users"
        description="Customer and admin accounts. Deactivating someone stops them signing in; nothing is deleted."
        actions={
          <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={() => setAdding(true)}>
            <UserPlus aria-hidden="true" />
            Add user
          </button>
        }
      />

      {!error && (
        <div className="adm-stats">
          <StatCard title="Users" value={stats.total} icon={<Users />} loading={loading} />
          <StatCard title="Active" value={stats.active} icon={<UserCheck />} loading={loading} />
          <StatCard title="Admins" value={stats.admins} icon={<ShieldCheck />} loading={loading} />
          <StatCard title="Can't sign in" value={stats.blocked} hint="Inactive or suspended" icon={<UserX />} loading={loading} />
        </div>
      )}

      <section className="adm-panel" aria-label="Users">
        {body}
      </section>

      <Drawer
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.fullName || 'User'}
        width={480}
        className="adm-drawer"
        footer={
          viewing && (
            <div className="pk-cluster" style={{ justifyContent: 'flex-end', padding: '4px 8px' }}>
              <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={() => setEditing(viewing)}>
                <Pencil aria-hidden="true" />
                Edit
              </button>
            </div>
          )
        }
      >
        {viewing && (
          <>
            <div className="adm-detail">
              <div className="pk-cluster" style={{ gap: 8 }}>
                <RoleBadge role={viewing.role} />
                <StatusBadge status={viewing.status} />
                {viewingIsSelf && <span className="pk-badge">You</span>}
              </div>
            </div>
            <div className="adm-detail">
              <h3 className="adm-detail__label">Contact</h3>
              <dl className="pk-rows">
                <div>
                  <dt>Mobile</dt>
                  <dd>{viewing.phone || '—'}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    {viewing.email || '—'}
                    {viewing.email && (
                      <div className="adm-cell-sub">{viewing.emailConfirmed ? 'Confirmed' : 'Not confirmed'}</div>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>GitHub</dt>
                  <dd>
                    <GithubLink username={viewing.gitHubUsername} />
                  </dd>
                </div>
              </dl>
            </div>
            <div className="adm-detail">
              <h3 className="adm-detail__label">Account</h3>
              <dl className="pk-rows">
                <div>
                  <dt>Joined</dt>
                  <dd>{formatDateTime(viewing.createdDate)}</dd>
                </div>
                <div>
                  <dt>Last sign-in</dt>
                  <dd>{hasDate(viewing.lastLoginDate) ? formatDateTime(viewing.lastLoginDate) : 'Never'}</dd>
                </div>
                <div>
                  <dt>User ID</dt>
                  <dd className="adm-num" style={{ fontSize: '0.8125rem' }}>
                    {viewing.id}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="adm-detail">
              <h3 className="adm-detail__label">Addresses</h3>
              {viewing.addresses?.length ? (
                <ul className="adm-list">
                  {viewing.addresses.map((a, index) => (
                    <li key={`${a.addressLine1}-${a.postalCode}-${index}`} style={{ display: 'block' }}>
                      <div className="adm-cell-title">
                        {a.contactPersonName || viewing.fullName}
                        {a.isDefault && (
                          <span className="pk-badge" style={{ marginLeft: 8 }}>
                            Default
                          </span>
                        )}
                      </div>
                      <div className="adm-cell-sub" style={{ fontSize: '0.8125rem' }}>
                        {[a.addressLine1, a.addressLine2, a.city, a.state, a.postalCode, a.country].filter(Boolean).join(', ')}
                      </div>
                      {a.contactPersonNumber && <div className="adm-cell-sub">{a.contactPersonNumber}</div>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="adm-note" style={{ margin: 0 }}>
                  No saved addresses.
                </p>
              )}
            </div>
          </>
        )}
      </Drawer>

      <Modal open={adding} onClose={() => setAdding(false)} title="Add user" footer={null} width={600} className="adm-modal" maskClosable={false}>
        {adding && (
          <AddUserForm
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              load();
            }}
          />
        )}
      </Modal>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={`Edit ${editing?.fullName || 'user'}`}
        footer={null}
        width={640}
        className="adm-modal"
        maskClosable={false}
      >
        {editing && (
          <EditUserForm
            user={editing}
            isSelf={editing.id === currentUserId}
            onCancel={() => setEditing(null)}
            onSaved={updated => {
              replaceUser(updated);
              setEditing(null);
              if (!updated) load();
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;
